import { useEffect, useState } from 'react';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, CalendarRange, Folder, Handshake, Plus, Search, User } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useClients } from '../hooks/useClients';
import { useCreateMeeting, useMeetings } from '../hooks/useMeetingLog';
import { usePermissions } from '../hooks/usePermissions';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import {
  meetingTypeLabel,
  type Meeting,
  type MeetingFilters,
  type MeetingInput,
} from '../lib/meetingLogApi';
import { ContactsTab } from '../components/meetings/ContactsTab';
import { MeetingFormModal } from '../components/meetings/MeetingFormModal';
import { IngestStatusBadge } from '../components/meetingNotes/IngestStatusBadge';
import { DateField } from '../components/ui/DateField';
import { MobileFilterSheet, type FilterDescriptor } from '../components/ui/MobileFilterSheet';
import { Select } from '../components/ui/Select';
import { SkeletonTable } from '../components/ui/Skeleton';

type Tab = 'meetings' | 'contacts';

const PAGE_SIZE = 20;

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ro-RO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * How long the meeting ran, as "1h 15m" / "45m". Null when there is no end —
 * an entry logged by hand often has only a start, and an invented duration
 * would read as fact.
 */
function formatDuration(startIso: string, endIso: string | null): string | null {
  if (!endIso) return null;
  const minutes = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60_000);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h ${rest}m` : `${rest}m`;
}

function attendeesLine(m: Meeting): string {
  return m.attendees
    .map((a) => (a.companyName ? `${a.fullName} (${a.companyName})` : a.fullName))
    .join(', ');
}

/**
 * "Întâlniri" — the team's log of meetings with external people, plus the
 * directory of those people. Gated on meetings.canView; the list itself is
 * scoped server-side ('all' vs. 'involved').
 */
export function MeetingsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canCreate = can('meetings', 'canCreate');
  const [params, setParams] = useSearchParams();

  const tab: Tab = params.get('tab') === 'contacts' ? 'contacts' : 'meetings';
  const [searchInput, setSearchInput] = useState(params.get('search') ?? '');
  const [showCreate, setShowCreate] = useState(false);

  const filters: MeetingFilters = {
    search: params.get('search') ?? undefined,
    clientId: params.get('clientId') ?? undefined,
    projectId: params.get('projectId') ?? undefined,
    leadId: params.get('leadId') ?? undefined,
    ownerId: params.get('ownerId') ?? undefined,
    contactId: params.get('contactId') ?? undefined,
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    page: Number(params.get('page') ?? '1') || 1,
    limit: PAGE_SIZE,
    sort: '-startAt',
  };

  function patch(next: Partial<Record<string, string | undefined>>, resetPage = true) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    if (resetPage) p.delete('page');
    setParams(p, { replace: true });
  }

  // Search as the user types (debounced); Enter still searches right away.
  const debouncedSearch = useDebouncedValue(searchInput.trim());
  useEffect(() => {
    if (debouncedSearch !== (params.get('search') ?? '')) {
      patch({ search: debouncedSearch || undefined });
    }
    // Only a settled keystroke triggers this, not every URL change.
  }, [debouncedSearch]);

  const { data, isLoading, isError } = useMeetings(filters);
  const meetings = data?.data ?? [];
  const meta = data?.meta;
  const hasFilters = Boolean(
    filters.search ||
    filters.clientId ||
    filters.projectId ||
    filters.ownerId ||
    filters.from ||
    filters.to ||
    filters.contactId ||
    filters.leadId,
  );

  const { data: clientsData } = useClients({ sort: 'name', limit: 100, active: 'true' });
  const { data: projectsData } = useProjects({ sort: 'name', limit: 100 });
  const { data: users } = useUsers();

  const createMutation = useCreateMeeting();

  const clientOptions = (clientsData?.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  const projectOptions = (projectsData?.projects ?? []).map((p) => ({
    value: p.id,
    label: p.code ? `${p.code} — ${p.name}` : p.name,
  }));
  const ownerOptions = (users ?? []).map((u) => ({ value: u.id, label: u.name }));

  // Phones: the four selects and the period go into the bottom sheet the
  // tasks pages use, one-to-one with the desktop controls; search stays in
  // the bar next to the trigger.
  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'select',
      id: 'client',
      label: t('meetings.filterClient'),
      icon: <Building2 size={16} />,
      value: filters.clientId ?? '',
      onChange: (v) => patch({ clientId: v || undefined }),
      options: clientOptions,
      placeholder: t('meetings.allClients'),
    },
    {
      kind: 'select',
      id: 'project',
      label: t('meetings.filterProject'),
      icon: <Folder size={16} />,
      value: filters.projectId ?? '',
      onChange: (v) => patch({ projectId: v || undefined }),
      options: projectOptions,
      placeholder: t('meetings.allProjects'),
    },
    {
      kind: 'select',
      id: 'owner',
      label: t('meetings.filterOwner'),
      icon: <User size={16} />,
      value: filters.ownerId ?? '',
      onChange: (v) => patch({ ownerId: v || undefined }),
      options: ownerOptions,
      placeholder: t('meetings.allOwners'),
    },
    {
      kind: 'date-range',
      id: 'period',
      label: t('meetings.filterPeriod'),
      icon: <CalendarRange size={16} />,
      from: filters.from ?? '',
      to: filters.to ?? '',
      onFromChange: (v) => patch({ from: v || undefined }),
      onToChange: (v) => patch({ to: v || undefined }),
    },
  ];
  const clearFilters = () => {
    setSearchInput('');
    patch({
      search: undefined,
      clientId: undefined,
      projectId: undefined,
      ownerId: undefined,
      contactId: undefined,
      leadId: undefined,
      from: undefined,
      to: undefined,
    });
  };

  async function handleCreate(input: MeetingInput) {
    const created = await createMutation.mutateAsync(input);
    setShowCreate(false);
    navigate(`/meetings/${created.id}`);
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Handshake className="text-brand-600" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('meetings.title')}</h1>
            <p className="text-sm text-gray-500">{t('meetings.subtitle')}</p>
          </div>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-fixed-white hover:bg-brand-700 sm:w-auto"
          >
            <Plus size={16} /> {t('meetings.new')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {(['meetings', 'contacts'] as Tab[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => patch({ tab: key === 'meetings' ? undefined : key }, false)}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                tab === key
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {key === 'meetings' ? t('meetings.tabMeetings') : t('meetings.tabContacts')}
              {key === 'meetings' && meta && (
                <span className="ml-1 text-xs text-gray-400">({meta.total})</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'contacts' ? (
        <div className="mt-4">
          <ContactsTab />
        </div>
      ) : (
        <>
          {/* Filters. Below md the search keeps the bar and everything else
              is behind the "Filtre" trigger; `md:contents` hands the desktop
              controls back to the six-column grid unchanged. */}
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-6">
            <div className="flex items-end gap-2 md:col-span-2">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  patch({ search: searchInput.trim() || undefined });
                }}
                className="min-w-0 flex-1"
              >
                <label className="ui-field-label">{t('common.search')}</label>
                <div className="relative">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t('meetings.search')}
                    className="min-h-[44px] w-full rounded border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 md:min-h-0"
                  />
                </div>
              </form>
              <MobileFilterSheet
                className="md:hidden"
                filters={mobileFilters}
                onClearAll={clearFilters}
              />
            </div>
            <div className="hidden md:contents">
              <Select
                label={t('meetings.filterClient')}
                searchable
                clearable
                value={filters.clientId ?? ''}
                onChange={(v) => patch({ clientId: v || undefined })}
                placeholder={t('meetings.allClients')}
                options={clientOptions}
              />
              <Select
                label={t('meetings.filterProject')}
                searchable
                clearable
                value={filters.projectId ?? ''}
                onChange={(v) => patch({ projectId: v || undefined })}
                placeholder={t('meetings.allProjects')}
                options={projectOptions}
              />
              <Select
                label={t('meetings.filterOwner')}
                searchable
                clearable
                value={filters.ownerId ?? ''}
                onChange={(v) => patch({ ownerId: v || undefined })}
                placeholder={t('meetings.allOwners')}
                options={ownerOptions}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="ui-field-label">{t('meetings.filterFrom')}</label>
                  <DateField
                    value={filters.from ?? ''}
                    onChange={(v) => patch({ from: v || undefined })}
                  />
                </div>
                <div>
                  <label className="ui-field-label">{t('meetings.filterTo')}</label>
                  <DateField
                    value={filters.to ?? ''}
                    onChange={(v) => patch({ to: v || undefined })}
                  />
                </div>
              </div>
            </div>
          </div>
          {filters.contactId && (
            <p className="mt-2 text-xs text-gray-500">
              {t('meetings.filteredByContact')}{' '}
              <button
                type="button"
                onClick={() => patch({ contactId: undefined })}
                className="text-brand-700 hover:underline"
              >
                {t('common.clearFilter')}
              </button>
            </p>
          )}
          {filters.leadId && (
            <p className="mt-2 text-xs text-gray-500">
              {t('meetings.filteredByLead')}{' '}
              <button
                type="button"
                onClick={() => patch({ leadId: undefined })}
                className="text-brand-700 hover:underline"
              >
                {t('common.clearFilter')}
              </button>
            </p>
          )}

          {/* List */}
          <div className="mt-4">
            {isLoading ? (
              <SkeletonTable rows={8} columns={6} />
            ) : isError ? (
              <p className="text-red-600">{t('meetings.error')}</p>
            ) : meetings.length === 0 ? (
              <p className="py-12 text-center text-gray-500">
                {hasFilters ? t('meetings.emptyFiltered') : t('meetings.empty')}
              </p>
            ) : (
              <>
                {/* Phones: one card per meeting. A six-column table only
                    scrolls sideways there, and the title is the last thing
                    you would find. */}
                <ul className="space-y-2 md:hidden">
                  {meetings.map((m) => {
                    const attendees = attendeesLine(m);
                    return (
                      <li key={m.id}>
                        <Link
                          to={`/meetings/${m.id}`}
                          className="block rounded-lg border border-gray-200 bg-white p-3 active:bg-gray-50"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 flex-1 font-medium text-gray-900">{m.title}</p>
                            <span className="shrink-0 whitespace-nowrap text-xs text-gray-500">
                              {formatWhen(m.startAt)}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-gray-500">
                            {meetingTypeLabel(m.meetingType)}
                            {m.client && ` · ${m.client.name}`}
                            {m.project && ` · ${m.project.code ?? m.project.name}`}
                            {m.lead && ` · ${m.lead.identifier}`}
                          </p>
                          {attendees && (
                            <p className="mt-1.5 line-clamp-2 text-sm text-gray-700">{attendees}</p>
                          )}
                          <div className="mt-2 flex items-center justify-between gap-2 text-xs text-gray-600">
                            <span className="truncate">
                              {m.owner.name}
                              {m.teamMembers.length > 0 && (
                                <span className="text-gray-400"> +{m.teamMembers.length}</span>
                              )}
                            </span>
                            {m.latestNote ? (
                              <span className="inline-flex shrink-0 items-center gap-1.5">
                                <IngestStatusBadge status={m.latestNote.ingestStatus as never} />
                                {m.notesCount > 1 && <span>×{m.notesCount}</span>}
                              </span>
                            ) : (
                              <span className="shrink-0 text-gray-400">
                                {t('meetings.noNotes')}
                              </span>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <div className="hidden overflow-x-auto rounded-lg border border-gray-200 bg-white md:block">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-2">{t('meetings.colDate')}</th>
                        <th className="px-4 py-2">{t('meetings.colDuration')}</th>
                        <th className="px-4 py-2">{t('meetings.colTitle')}</th>
                        <th className="px-4 py-2">{t('meetings.colAttendees')}</th>
                        <th className="px-4 py-2">{t('meetings.colContext')}</th>
                        <th className="px-4 py-2">{t('meetings.colOwner')}</th>
                        <th className="px-4 py-2">{t('meetings.colNotes')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {meetings.map((m) => (
                        <tr
                          key={m.id}
                          onClick={() => navigate(`/meetings/${m.id}`)}
                          className="cursor-pointer hover:bg-gray-50"
                        >
                          <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                            {formatWhen(m.startAt)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-700">
                            {formatDuration(m.startAt, m.endAt) ?? (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            <Link
                              to={`/meetings/${m.id}`}
                              className="font-medium text-gray-900 hover:text-brand-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {m.title}
                            </Link>
                            <div className="text-xs text-gray-500">
                              {meetingTypeLabel(m.meetingType)}
                            </div>
                          </td>
                          <td className="max-w-xs px-4 py-2 text-gray-700">
                            <span className="line-clamp-2">{attendeesLine(m) || '—'}</span>
                          </td>
                          <td className="px-4 py-2 text-gray-700">
                            <div className="flex flex-col">
                              {m.client && <span>{m.client.name}</span>}
                              {m.project && (
                                <span className="text-xs text-gray-500">
                                  {m.project.code ?? m.project.name}
                                </span>
                              )}
                              {m.lead && (
                                <span className="text-xs text-gray-500">
                                  <span className="font-mono">{m.lead.identifier}</span> ·{' '}
                                  {m.lead.title}
                                </span>
                              )}
                              {!m.client && !m.project && !m.lead && (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-gray-700">
                            {m.owner.name}
                            {m.teamMembers.length > 0 && (
                              <span className="text-xs text-gray-400">
                                {' '}
                                +{m.teamMembers.length}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2">
                            {m.latestNote ? (
                              <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                                <IngestStatusBadge status={m.latestNote.ingestStatus as never} />
                                {m.notesCount > 1 && <span>×{m.notesCount}</span>}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">{t('meetings.noNotes')}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {meta && meta.totalPages > 1 && (
              <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
                <span>{t('common.pageOf', { page: meta.page, total: meta.totalPages })}</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={meta.page <= 1}
                    onClick={() => patch({ page: String(meta.page - 1) }, false)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    type="button"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => patch({ page: String(meta.page + 1) }, false)}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showCreate && (
        <MeetingFormModal
          submitting={createMutation.isPending}
          onClose={() => setShowCreate(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}
